import React, { useState, useEffect, useMemo } from "react";
import {
  saveFeeStructure,
  saveFeeStructurePortal,
  getAllFeeHeads,
  getAllFeeHeadsPortal,
} from "../../api/fees/feeApi";
import {
  Modal,
  Button,
  Input,
  Select,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

/**
 * Lightweight structure editor: pick class + year + one or more fee head amounts.
 * Full multi-class UI remains on FeeStructurePage; this modal is for quick entry.
 */
const FeeStructureModal = ({
  isOpen,
  onClose,
  onSuccess,
  isPortal = false,
  academicYearId = "",
  classId = "",
}) => {
  const [heads, setHeads] = useState([]);
  const [form, setForm] = useState({
    academicYearId: "",
    classId: "",
    feeHeadId: "",
    amount: "",
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      academicYearId: academicYearId || "",
      classId: classId || "",
      feeHeadId: "",
      amount: "",
    });
    (async () => {
      try {
        const res = isPortal
          ? await getAllFeeHeadsPortal({ isActive: true })
          : await getAllFeeHeads({ isActive: true });
        setHeads(
          (res.data || res || []).filter(
            (h) => h.source !== "TRANSPORT_MODULE" && h.type !== "TRANSPORT"
          )
        );
      } catch {
        setHeads([]);
      }
    })();
  }, [isOpen, academicYearId, classId, isPortal]);

  const feeHeadOptions = useMemo(
    () => [
      { label: "Select fee head", value: "" },
      ...heads.map((h) => ({
        label: `${h.name} (${h.code})`,
        value: h._id,
      })),
    ],
    [heads]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.academicYearId || !form.classId || !form.feeHeadId || !form.amount) {
      toast.error("All fields are required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        academicYearId: form.academicYearId,
        classId: form.classId,
        feeHeadId: form.feeHeadId,
        amount: Number(form.amount),
      };
      if (isPortal) await saveFeeStructurePortal(payload);
      else await saveFeeStructure(payload);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Fee Structure Entry"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="fee-structure-form"
            variant="primary"
            isLoading={loading}
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="fee-structure-form"
        onSubmit={handleSubmit}
        className="space-y-4 pt-1"
      >
        

        <Input
          label="Academic Year ID *"
          placeholder="Enter Academic Year ID"
          value={form.academicYearId}
          onChange={(e) =>
            setForm((p) => ({ ...p, academicYearId: e.target.value }))
          }
          required
        />

        <Input
          label="Class ID *"
          placeholder="Enter Class ID"
          value={form.classId}
          onChange={(e) =>
            setForm((p) => ({ ...p, classId: e.target.value }))
          }
          required
        />

        <Select
          label="Fee Head *"
          options={feeHeadOptions}
          value={form.feeHeadId}
          onChange={(val) => setForm((p) => ({ ...p, feeHeadId: val }))}
          required
        />

        <Input
          label="Amount *"
          type="number"
          min="0"
          step="0.01"
          placeholder="0.00"
          leftIcon={<span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">₹</span>}
          value={form.amount}
          onChange={(e) =>
            setForm((p) => ({ ...p, amount: e.target.value }))
          }
          required
        />
      </form>
    </Modal>
  );
};

export default FeeStructureModal;